import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Save,
    AlertCircle,
    CheckCircle,
    Clock,
    Bell,
    Play,
    Loader2,
    Mail,
    Plus,
    X,
    Shield,
    Zap,
    Calendar,
    Bot
} from 'lucide-react';
import { apiClient, LicenceCheckSettings, UpdateLicenceCheckSettingsRequest, TestSchedulerResponse } from '../services/api';
import { useScoringConfig } from '../contexts/ScoringConfigContext';

interface AutomationSettingsProps {
    user: any;
    onSettingsChange?: (settings: any) => void;
}

const CARD = 'bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]';

const AutomationSettings: React.FC<AutomationSettingsProps> = ({ user, onSettingsChange }) => {
    const location = useLocation();
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();

    const isDemoOrg = user?.organisation?.demoMode === true;

    const [settings, setSettings] = useState<LicenceCheckSettings | null>(null);
    const [originalSettings, setOriginalSettings] = useState<LicenceCheckSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [highlightConsentSection, setHighlightConsentSection] = useState(false);
    const [testResult, setTestResult] = useState<TestSchedulerResponse | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingSettings, setPendingSettings] = useState<UpdateLicenceCheckSettingsRequest | null>(null);
    const [showRunNowConfirmation, setShowRunNowConfirmation] = useState(false);

    useEffect(() => {
        if (location.hash === '#consent-check') {
            setTimeout(() => {
                setHighlightConsentSection(true);
                setTimeout(() => setHighlightConsentSection(false), 2500);
            }, 150);
        }
    }, [location.hash]);

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSettings = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getAutomationSettings();
            let loadedSettings = {
                ...response,
                notificationEmailRecipients: response.notificationEmailRecipients || []
            };
            if (isDemoOrg) {
                loadedSettings = { ...loadedSettings, enabled: true, autoCheckOnConsent: true };
            }
            setSettings(loadedSettings);
            setOriginalSettings(loadedSettings);
        } catch (err: any) {
            setError(err.message || 'Failed to load automation settings');
            console.error('Error loading settings:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!settings) return;
        try {
            setIsSaving(true);
            setError(null);
            setSuccessMessage(null);
            const updateRequest: UpdateLicenceCheckSettingsRequest = {
                enabled: settings.enabled,
                defaultFrequencyMonths: settings.defaultFrequencyMonths,
                preExpiryCheckEnabled: settings.preExpiryCheckEnabled,
                preExpiryCheckDays: settings.preExpiryCheckDays,
                highRiskFrequencyMonths: settings.highRiskFrequencyMonths,
                mediumRiskFrequencyMonths: settings.mediumRiskFrequencyMonths,
                lowRiskFrequencyMonths: settings.lowRiskFrequencyMonths,
                preferredCheckTime: settings.preferredCheckTime,
                timezone: 'UTC',
                immediateAlertsEnabled: settings.immediateAlertsEnabled,
                dailyDigestEnabled: settings.dailyDigestEnabled,
                dailyDigestTime: settings.dailyDigestTime,
                weeklySummaryEnabled: settings.weeklySummaryEnabled,
                weeklySummaryDay: settings.weeklySummaryDay,
                notificationEmailRecipients: settings.notificationEmailRecipients || [],
                autoCheckOnConsent: settings.autoCheckOnConsent
            };
            setPendingSettings(updateRequest);
            if (settings.enabled) {
                const coverage = await apiClient.testAutomationScheduler();
                setTestResult(coverage);
            }
            setShowConfirmation(true);
        } catch (err: any) {
            setError(err.message || 'Failed to load coverage summary');
        } finally {
            setIsSaving(false);
        }
    };

    const confirmSave = async () => {
        if (!pendingSettings) return;
        try {
            setIsSaving(true);
            setError(null);
            const updated = await apiClient.updateAutomationSettings(pendingSettings);
            setSettings(updated);
            setOriginalSettings(updated);
            onSettingsChange?.(updated);
            setSuccessMessage('Automation settings saved successfully');
            setShowConfirmation(false);
            setPendingSettings(null);
            setTestResult(null);
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to save automation settings');
        } finally {
            setIsSaving(false);
        }
    };

    const cancelSave = () => {
        setShowConfirmation(false);
        setPendingSettings(null);
        setTestResult(null);
    };

    const handleRunNow = () => setShowRunNowConfirmation(true);

    const confirmRunNow = async () => {
        try {
            setIsRunning(true);
            setError(null);
            setShowRunNowConfirmation(false);
            const result = await apiClient.runAutomationNow();
            setSuccessMessage(`Automated check initiated for ${result.driversQueued} drivers`);
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err: any) {
            setError(err.message || 'Failed to run automated check');
        } finally {
            setIsRunning(false);
        }
    };

    const cancelRunNow = () => setShowRunNowConfirmation(false);

    const addEmail = () => {
        if (!settings) return;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailInput)) { setError('Please enter a valid email address'); return; }
        const recipients = settings.notificationEmailRecipients || [];
        if (recipients.includes(emailInput)) { setError('Email already in the list'); return; }
        setSettings({ ...settings, notificationEmailRecipients: [...recipients, emailInput] });
        setEmailInput('');
        setError(null);
    };

    const removeEmail = (email: string) => {
        if (!settings) return;
        const recipients = settings.notificationEmailRecipients || [];
        setSettings({ ...settings, notificationEmailRecipients: recipients.filter(e => e !== email) });
    };

    const hasChanges = (): boolean => {
        if (!settings || !originalSettings) return false;
        return JSON.stringify(settings) !== JSON.stringify(originalSettings);
    };

    const toggleSwitch = (checked: boolean, onChange: (v: boolean) => void, disabled = false) => (
        <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => !disabled && onChange(e.target.checked)}
                className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
        </label>
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-700">Failed to load automation settings</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">

            {/* ── Save Confirmation Modal ── */}
            {showConfirmation && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                                    <Save className="w-6 h-6" style={{ color: '#69BE28' }} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Review Automation Settings</h2>
                                    <p className="text-sm text-gray-500">Check the impact before saving</p>
                                </div>
                            </div>
                            <button
                                onClick={cancelSave}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">

                            {/* Consent-Triggered Checks */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Consent-Triggered Checks</h3>
                                <div className={`p-4 rounded-lg border flex items-center gap-3 ${settings.autoCheckOnConsent ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                                    {settings.autoCheckOnConsent
                                        ? <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                        : <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    }
                                    <p className={`text-sm ${settings.autoCheckOnConsent ? 'text-blue-700' : 'text-gray-600'}`}>
                                        {settings.autoCheckOnConsent ? (
                                            <>Consent-triggered checks enabled</>
                                        ) : (
                                            'Consent-triggered checks disabled'
                                        )}
                                    </p>
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Scheduled Automation */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Scheduled Automation</h3>

                                {settings.enabled && testResult && (
                                    <>
                                        <div className="grid grid-cols-3 gap-4 mb-4">
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-600 mb-1">Total Drivers</p>
                                                <p className="text-2xl font-bold text-gray-900">{testResult.totalDrivers}</p>
                                            </div>
                                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                                <p className="text-sm text-green-700 mb-1 font-medium">✓ Will Be Checked</p>
                                                <p className="text-2xl font-bold text-green-700">{testResult.eligible.count}</p>
                                                <p className="text-xs text-green-600 mt-1">Active with consent</p>
                                            </div>
                                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                                <p className="text-sm text-red-700 mb-1 font-medium">✗ Excluded</p>
                                                <p className="text-2xl font-bold text-red-700">{testResult.excluded.noConsent + testResult.excluded.inactive}</p>
                                                <p className="text-xs text-red-600 mt-1">
                                                    {testResult.excluded.noConsent > 0 && `${testResult.excluded.noConsent} no consent`}
                                                    {testResult.excluded.noConsent > 0 && testResult.excluded.inactive > 0 && ', '}
                                                    {testResult.excluded.inactive > 0 && `${testResult.excluded.inactive} inactive`}
                                                </p>
                                            </div>
                                        </div>

                                        {testResult.eligible.count > 0 && (
                                            <div className="mb-4">
                                                <p className="text-xs font-medium text-gray-500 mb-3">Eligible drivers by risk level</p>
                                                <div className="grid grid-cols-4 gap-3">
                                                    <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                                        <p className="text-xs text-red-700 font-medium">High Risk</p>
                                                        <p className="text-xl font-bold text-red-700">{testResult.eligible.riskBreakdown.high}</p>
                                                        <p className="text-xs text-red-600 mt-1">Every {settings.highRiskFrequencyMonths} month{settings.highRiskFrequencyMonths > 1 ? 's' : ''}</p>
                                                    </div>
                                                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                                        <p className="text-xs text-yellow-700 font-medium">Medium Risk</p>
                                                        <p className="text-xl font-bold text-yellow-700">{testResult.eligible.riskBreakdown.medium}</p>
                                                        <p className="text-xs text-yellow-600 mt-1">Every {settings.mediumRiskFrequencyMonths} month{settings.mediumRiskFrequencyMonths > 1 ? 's' : ''}</p>
                                                    </div>
                                                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                                        <p className="text-xs text-green-700 font-medium">Low Risk</p>
                                                        <p className="text-xl font-bold text-green-700">{testResult.eligible.riskBreakdown.low}</p>
                                                        <p className="text-xs text-green-600 mt-1">Every {settings.lowRiskFrequencyMonths} month{settings.lowRiskFrequencyMonths > 1 ? 's' : ''}</p>
                                                    </div>
                                                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                        <p className="text-xs text-gray-700 font-medium">Unknown Risk</p>
                                                        <p className="text-xl font-bold text-gray-700">{testResult.eligible.riskBreakdown.unknown}</p>
                                                        <p className="text-xs text-gray-600 mt-1">Every {settings.defaultFrequencyMonths} month{settings.defaultFrequencyMonths > 1 ? 's' : ''}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className={`p-4 rounded-lg border flex items-center gap-3 ${settings.enabled ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                                    {settings.enabled
                                        ? <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        : <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                                    }
                                    <p className={`text-sm ${settings.enabled ? 'text-green-700' : 'text-yellow-700'}`}>
                                        {settings.enabled
                                            ? `Scheduled checks enabled, runs daily at ${settings.preferredCheckTime} (24h)`
                                            : 'Scheduled automation is disabled'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                                <button
                                    onClick={cancelSave}
                                    disabled={isSaving}
                                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmSave}
                                    disabled={isSaving}
                                    className="px-6 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {isSaving ? (
                                        <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></>
                                    ) : (
                                        <><Save className="w-4 h-4" /><span>Confirm & Save</span></>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Run Now Confirmation Modal ── */}
            {showRunNowConfirmation && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Play className="w-5 h-5 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Run Automated Checks Now?</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                            This will immediately check all active drivers with consent, regardless of when they were last checked.
                        </p>
                        <p className="text-sm text-gray-500 mb-6">
                            This bypasses your scheduled automation and may take a few minutes to complete.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelRunNow}
                                disabled={isRunning}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRunNow}
                                disabled={isRunning}
                                className="px-4 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                {isRunning ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /><span>Running...</span></>
                                ) : (
                                    <><Play className="w-4 h-4" /><span>Run Now</span></>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Messages ── */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-800">{error}</p>
                </div>
            )}
            {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p className="text-sm text-green-800">{successMessage}</p>
                </div>
            )}

            {/* ── Demo banner ── */}
            {isDemoOrg && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-blue-900">Demo Mode: View Only</p>
                        <p className="text-sm text-blue-800 mt-0.5">
                            These settings are displayed for preview purposes only and cannot be modified. Contact Sales to enable this feature.
                        </p>
                    </div>
                </div>
            )}

            {/* ── Zone 1: Slim page header ── */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <Bot className="w-4 h-4" style={{ color: '#69BE28' }} />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">Automated Checks</h2>
                        <p className="text-xs text-gray-500">Configure automatic DVLA licence verification for your drivers</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleRunNow}
                        disabled={isRunning || !settings.enabled || isDemoOrg}
                        className="px-3 py-1.5 rounded-lg text-sm text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        Run Now
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges() || isDemoOrg}
                        className="px-3 py-1.5 rounded-lg text-sm text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save Changes
                    </button>
                </div>
            </div>

            {/* ── Zone 2: Consent-Triggered Checks ── */}
            <div
                id="consent-check"
                className={`${CARD} p-6 transition-all duration-700`}
                style={highlightConsentSection
                    ? { border: '2px solid #69BE28', boxShadow: '0 0 0 4px rgba(105,190,40,0.15)' }
                    : undefined}
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                        <Zap className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Consent-Triggered Checks</h3>
                        <p className="text-xs text-gray-500">Triggered immediately when a driver approves their consent form, independent of scheduling</p>
                    </div>
                </div>

                {(() => {
                    const addOnEnabled = !!user?.organisation?.automatedChecks;
                    return (
                    <div className="bg-gray-50/80 rounded-lg border border-gray-100 p-4">
                        <div className="flex items-center justify-between gap-4">
                            <p className={`text-sm font-medium ${addOnEnabled ? 'text-gray-900' : 'text-gray-400'}`}>Check licence when consent is approved</p>
                            {toggleSwitch(
                                addOnEnabled ? (settings.autoCheckOnConsent ?? true) : false,
                                (v) => setSettings({ ...settings, autoCheckOnConsent: v }),
                                isDemoOrg || !addOnEnabled
                            )}
                        </div>



                    </div>
                    );
                })()}
            </div>

            {/* ── Zone 3: Scheduled Automation ── */}
            <div className={`${CARD} overflow-hidden`}>

                {/* Card header */}
                <div className="px-6 py-5 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                                <Calendar className="w-4 h-4" style={{ color: '#69BE28' }} />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Scheduled Automation</h3>
                                <p className="text-xs text-gray-500">Periodic checks based on risk levels and configured frequencies</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {settings.enabled && (
                                <div className="text-right hidden sm:block">
                                    {settings.nextScheduledRun && (
                                        <p className="text-xs text-gray-500">Next: {new Date(settings.nextScheduledRun).toLocaleString()}</p>
                                    )}
                                    {settings.lastRunDate && (
                                        <p className="text-xs text-gray-400">Last: {new Date(settings.lastRunDate).toLocaleString()}</p>
                                    )}
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{user?.organisation?.automatedChecks && settings.enabled ? 'Enabled' : 'Disabled'}</span>
                                {toggleSwitch(
                                    !!user?.organisation?.automatedChecks && settings.enabled,
                                    (v) => setSettings({ ...settings, enabled: v }),
                                    isDemoOrg || !user?.organisation?.automatedChecks
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card body */}
                {settings.enabled ? (
                    <div className="p-6">
                        <div className="grid grid-cols-2 gap-6">

                            {/* ── Left column ── */}
                            <div className="space-y-6">

                                {/* Risk-Based Frequencies */}
                                <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Shield className="w-4 h-4" style={{ color: '#69BE28' }} />
                                        <h4 className="text-sm font-semibold text-gray-900">Check Frequencies by Risk Level</h4>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                                                <label className="text-xs font-medium text-red-900">High Risk (score {mediumHighBoundary}+)</label>
                                            </div>
                                            <select
                                                value={settings.highRiskFrequencyMonths}
                                                onChange={(e) => setSettings({ ...settings, highRiskFrequencyMonths: parseInt(e.target.value) })}
                                                disabled={isDemoOrg}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <option value={1}>Every month</option>
                                                <option value={3}>Every 3 months</option>
                                                <option value={6}>Every 6 months</option>
                                                <option value={12}>Every 12 months</option>
                                            </select>
                                        </div>

                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                                                <label className="text-xs font-medium text-yellow-900">Medium Risk (score {lowMediumBoundary}–{mediumHighBoundary - 1})</label>
                                            </div>
                                            <select
                                                value={settings.mediumRiskFrequencyMonths}
                                                onChange={(e) => setSettings({ ...settings, mediumRiskFrequencyMonths: parseInt(e.target.value) })}
                                                disabled={isDemoOrg}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <option value={1}>Every month</option>
                                                <option value={3}>Every 3 months</option>
                                                <option value={6}>Every 6 months</option>
                                                <option value={12}>Every 12 months</option>
                                            </select>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                                                <label className="text-xs font-medium text-green-900">Low Risk (score &lt;{lowMediumBoundary})</label>
                                            </div>
                                            <select
                                                value={settings.lowRiskFrequencyMonths}
                                                onChange={(e) => setSettings({ ...settings, lowRiskFrequencyMonths: parseInt(e.target.value) })}
                                                disabled={isDemoOrg}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <option value={1}>Every month</option>
                                                <option value={3}>Every 3 months</option>
                                                <option value={6}>Every 6 months</option>
                                                <option value={12}>Every 12 months</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Check Rules */}
                                <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <AlertCircle className="w-4 h-4" style={{ color: '#69BE28' }} />
                                        <h4 className="text-sm font-semibold text-gray-900">Additional Check Rules</h4>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="bg-gray-50/80 border border-gray-100 rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                New &amp; Unassessed Drivers
                                            </label>
                                            <select
                                                value={settings.defaultFrequencyMonths}
                                                onChange={(e) => setSettings({ ...settings, defaultFrequencyMonths: parseInt(e.target.value) })}
                                                disabled={isDemoOrg}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <option value={1}>Every month</option>
                                                <option value={3}>Every 3 months</option>
                                                <option value={6}>Every 6 months</option>
                                                <option value={12}>Every 12 months</option>
                                            </select>
                                        </div>

                                        <div className="bg-gray-50/80 border border-gray-100 rounded-lg p-4">
                                            <div className="flex items-center justify-between gap-4">
                                                <p className="text-sm font-medium text-gray-700">Pre-Expiry Safety Checks</p>
                                                {toggleSwitch(
                                                    settings.preExpiryCheckEnabled,
                                                    (v) => setSettings({ ...settings, preExpiryCheckEnabled: v }),
                                                    isDemoOrg
                                                )}
                                            </div>
                                            {settings.preExpiryCheckEnabled && (
                                                <div className="mt-3 pt-3 border-t border-gray-200">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Days before expiry</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="365"
                                                        value={settings.preExpiryCheckDays}
                                                        onChange={(e) => setSettings({ ...settings, preExpiryCheckDays: parseInt(e.target.value) })}
                                                        disabled={isDemoOrg}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Right column ── */}
                            <div className="space-y-6">

                                {/* Scheduling */}
                                <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Clock className="w-4 h-4" style={{ color: '#69BE28' }} />
                                        <h4 className="text-sm font-semibold text-gray-900">Scheduling</h4>
                                    </div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Daily check time (24h)
                                    </label>
                                    <input
                                        type="time"
                                        step="1"
                                        value={settings.preferredCheckTime}
                                        onChange={(e) => setSettings({ ...settings, preferredCheckTime: e.target.value })}
                                        disabled={isDemoOrg}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                </div>

                                {/* Notifications */}
                                <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Bell className="w-4 h-4" style={{ color: '#69BE28' }} />
                                        <h4 className="text-sm font-semibold text-gray-900">Notifications</h4>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="bg-gray-50/80 border border-gray-100 rounded-lg p-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">Immediate Alerts</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">Send alerts for high-risk findings immediately</p>
                                                </div>
                                                {toggleSwitch(
                                                    settings.immediateAlertsEnabled,
                                                    (v) => setSettings({ ...settings, immediateAlertsEnabled: v }),
                                                    isDemoOrg
                                                )}
                                            </div>
                                        </div>

                                        <div className="bg-gray-50/80 border border-gray-100 rounded-lg p-4 space-y-3">
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">Daily Digest</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">Daily summary of automated checks</p>
                                                </div>
                                                {toggleSwitch(
                                                    settings.dailyDigestEnabled,
                                                    (v) => setSettings({ ...settings, dailyDigestEnabled: v }),
                                                    isDemoOrg
                                                )}
                                            </div>
                                            {settings.dailyDigestEnabled && (
                                                <div className="pt-3 border-t border-gray-200">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Digest Time (24h)</label>
                                                    <input
                                                        type="time"
                                                        step="1"
                                                        value={settings.dailyDigestTime}
                                                        onChange={(e) => setSettings({ ...settings, dailyDigestTime: e.target.value })}
                                                        disabled={isDemoOrg}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div className="bg-gray-50/80 border border-gray-100 rounded-lg p-4 space-y-3">
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">Weekly Summary</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">Weekly trends and statistics</p>
                                                </div>
                                                {toggleSwitch(
                                                    settings.weeklySummaryEnabled,
                                                    (v) => setSettings({ ...settings, weeklySummaryEnabled: v }),
                                                    isDemoOrg
                                                )}
                                            </div>
                                            {settings.weeklySummaryEnabled && (
                                                <div className="pt-3 border-t border-gray-200">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Summary Day</label>
                                                    <select
                                                        value={settings.weeklySummaryDay}
                                                        onChange={(e) => setSettings({ ...settings, weeklySummaryDay: parseInt(e.target.value) })}
                                                        disabled={isDemoOrg}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    >
                                                        <option value={0}>Sunday</option>
                                                        <option value={1}>Monday</option>
                                                        <option value={2}>Tuesday</option>
                                                        <option value={3}>Wednesday</option>
                                                        <option value={4}>Thursday</option>
                                                        <option value={5}>Friday</option>
                                                        <option value={6}>Saturday</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Email Recipients */}
                                <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Mail className="w-4 h-4" style={{ color: '#69BE28' }} />
                                        <h4 className="text-sm font-semibold text-gray-900">Notification Recipients</h4>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex gap-2">
                                            <input
                                                type="email"
                                                value={emailInput}
                                                onChange={(e) => setEmailInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && !isDemoOrg && addEmail()}
                                                placeholder="email@example.com"
                                                disabled={isDemoOrg}
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                            />
                                            <button
                                                onClick={addEmail}
                                                disabled={isDemoOrg}
                                                className="px-3 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                style={{ backgroundColor: '#69BE28' }}
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {(settings.notificationEmailRecipients || []).length > 0 ? (
                                            <div className="flex flex-wrap gap-2">
                                                {(settings.notificationEmailRecipients || []).map((email) => (
                                                    <span key={email} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                                                        <Mail className="w-3 h-3 mr-1.5" />
                                                        {email}
                                                        <button
                                                            onClick={() => removeEmail(email)}
                                                            disabled={isDemoOrg}
                                                            className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-400 text-center py-3">No recipients added yet</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="py-14 px-6 flex flex-col items-center text-center">
                        <div className="p-3 rounded-full bg-gray-100 mb-4">
                            <Clock className="w-8 h-8 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">Scheduled automation is disabled</p>
                        <p className="text-xs text-gray-400 mt-1 max-w-xs">
                            Enable the toggle above to configure risk-based frequencies, scheduling, and notification settings
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AutomationSettings;
